import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { toUserMessage } from '@platform/api-client';

import { PATHS } from '@/shared/constants/app/routes.constant.ts';

import {
  useCreatePumsemItem,
  usePumsemRevision,
  useRemovePumsemItem,
  useUpdatePumsemItem,
} from '../../api/use-pumsem-manage.query';
import { PumsemBodyEditor } from '../../components/manage/PumsemBodyEditor';
import { insertBlock, unplacedNotes } from '../../lib/pumsemBody';
import { parseHeadingLine, splitNotes } from '../../lib/pumsemPaste';
import {
  BTN_DANGER,
  BTN_GHOST,
  BTN_PRIMARY,
  FIELD,
  LABEL,
} from '../../components/manage/pumsemFormStyles';
import type {
  PumsemApplicability,
  PumsemBlock,
} from '../../model/pumsem.type';
import type { PumsemItemInput } from '../../model/pumsemManage.type';
import { statusOf } from '../../lib/pumsemError';

interface FormState {
  /** 신설에서는 빈 값이다 — 서버가 소속 절 아래 다음 번호를 매긴다. */
  code: string;
  title: string;
  unit: string;
  /** 제목 괄호 안 원문 표기. 쉼표로 이어 적는다. */
  marksText: string;
  changeSummary: string;
  /**
   * 본문에 배치하지 **않은** [주]. 줄 하나가 원소 하나다. 본문 `[주]` 블록에 놓인
   * 것은 본문 편집기가 들고 있고, 서버가 둘을 이어 붙여 항목 `notes` 를 만든다.
   */
  notesText: string;
  includesText: string;
  excludesText: string;
  scope: string;
  body: PumsemBlock[];
}

const EMPTY: FormState = {
  code: '',
  title: '',
  unit: '',
  marksText: '',
  changeSummary: '',
  notesText: '',
  includesText: '',
  excludesText: '',
  scope: '',
  body: [],
};

/** 빈 줄은 버린다. 사용자가 문단을 띄우려고 넣은 줄이 [주] 항목이 되면 안 된다. */
const toLines = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

/**
 * 항목 편집 폼.
 *
 * 신설과 수정이 한 화면이다 — 입력 항목이 완전히 같고, 두 화면으로 가르면
 * 본문 편집기를 두 번 붙여야 한다(도움말 문서 폼과 같은 구조).
 *
 * 신설일 때 판·부문·소속 절은 URL 쿼리(`?edition=&division=&group=`)로 받는다.
 * 관리 트리에서 `+ 항목 추가` 를 누른 자리가 이미 그 셋을 정했고, 폼에서 다시
 * 고르게 하면 트리에서 고른 것과 어긋날 수 있다.
 *
 * **코드는 비워서 보낸다** — 서버가 그 절 아래 다음 번호를 매긴다. 손으로 치게
 * 두면 오타가 그대로 딥링크가 된다.
 */
/** 지워졌거나 이 판에 없는 항목. 장애와 갈라서 다른 문구를 준다. */
const NOT_FOUND = 404;

export function PumsemItemFormView() {
  const { revisionId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = !!revisionId;
  const {
    data: revision,
    isPending,
    isError: loadFailed,
    error: loadError,
  } = usePumsemRevision(revisionId);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  // 서버 값이 도착하면 폼을 한 번 채운다. effect 대신 파생 상태 —
  // 사용자가 이미 고치고 있는 값을 재조회가 덮어쓰면 안 된다.
  if (revision && loadedId !== revision.id) {
    setLoadedId(revision.id);
    setForm({
      code: revision.code,
      title: revision.title,
      unit: revision.unit ?? '',
      marksText: revision.revisionMarks.join(', '),
      changeSummary: revision.changeSummary ?? '',
      // 서버가 준 notes 는 [본문에 놓인 것, 배치 안 한 것] 순서다. 앞쪽까지 여기
      // 담으면 저장할 때 본문 것과 겹쳐 두 번 들어간다.
      notesText: unplacedNotes(revision.notes, revision.body).join('\n'),
      includesText: revision.applicability.includes.join('\n'),
      excludesText: revision.applicability.excludes.join('\n'),
      scope: revision.applicability.scope ?? '',
      body: revision.body,
    });
  }

  const editionSlug = revision?.edition.slug ?? params.get('edition') ?? '';
  const divisionSlug = revision?.division.slug ?? params.get('division') ?? '';
  const groupCode = revision?.groupCode ?? params.get('group') ?? '';

  const createItem = useCreatePumsemItem(editionSlug);
  const updateItem = useUpdatePumsemItem(revisionId ?? '');
  const removeItem = useRemovePumsemItem();

  const [error, setError] = useState<string | null>(null);
  /** 방금 저장한 코드. 연속 입력에서 "어디까지 옮겼는지"를 화면이 기억한다. */
  const [saved, setSaved] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [leaving, setLeaving] = useState(false);
  /** 폼을 한 글자라도 건드렸는지. 이탈 확인의 기준이다. */
  const [dirty, setDirty] = useState(false);

  /**
   * 폼을 고치면 직전 저장 실패 메시지를 지운다.
   *
   * effect 로 `form` 을 지켜보면 렌더가 한 번 더 돌고, 서버 값을 채우는 위
   * 분기에서도 헛되이 돈다. 값을 바꾸는 곳이 여기 하나뿐이라 여기서 지운다.
   */
  const patchForm = (next: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...next }));
    setError(null);
    setDirty(true);
  };

  if (isEdit && isPending) {
    return (
      <p className="py-24 text-center text-[14px] text-neutral-500">
        불러오는 중…
      </p>
    );
  }

  /*
    조회가 실패했는데 폼을 그리면 **내용이 다 날아간 수정 화면**이 된다 —
    제목은 `품셈 항목 수정`, 코드·품셈명·본문은 전부 비어 있고 하단에는
    "저장하려면 … 이(가) 필요합니다" 만 뜬다. `retry: false` 라 저절로
    회복되지도 않는다. 관리자는 데이터가 지워진 줄 안다.
  */
  if (isEdit && loadFailed) {
    return (
      <div className="px-6 py-24 text-center">
        <p className="text-[15px] font-bold text-neutral-800 dark:text-neutral-100">
          {statusOf(loadError) === NOT_FOUND
            ? '이 항목은 이 판에 없습니다.'
            : '항목을 불러오지 못했습니다.'}
        </p>
        <p className="mt-2 text-[13.5px] text-neutral-500">
          {statusOf(loadError) === NOT_FOUND
            ? '다른 판에서 제외됐거나 코드가 바뀌었을 수 있습니다. 관리 목록에서 다시 여세요.'
            : '로그인이 풀렸을 수 있습니다. 잠시 후 다시 시도해 주세요.'}
        </p>
        <button
          type="button"
          className={`${BTN_GHOST} mt-4`}
          onClick={() => navigate(PATHS.PUMSEM_MANAGE)}
        >
          관리 목록으로
        </button>
      </div>
    );
  }

  const applicability: PumsemApplicability = {
    includes: toLines(form.includesText),
    excludes: toLines(form.excludesText),
    scope: form.scope.trim() || null,
  };

  const payload: PumsemItemInput = {
    division: divisionSlug,
    // 수정에서는 화면에 뜬 코드를 그대로 보낸다(바꿨으면 서버가 리다이렉트를
    // 남긴다). 신설에서는 비워서 서버가 매기게 한다.
    ...(isEdit ? { code: form.code.trim() } : { groupCode }),
    title: form.title.trim(),
    // 서술 항목(적용기준 계열)은 단위가 없다 — 빈 문자열이 아니라 null 이다.
    unit: form.unit.trim() || null,
    revisionMarks: form.marksText
      .split(',')
      .map((mark) => mark.trim())
      .filter(Boolean),
    body: form.body,
    notes: toLines(form.notesText),
    applicability,
    changeSummary: form.changeSummary.trim() || null,
  };

  // 저장 버튼이 이유 없이 죽어 있으면 안 된다 — 빠진 필드를 화면에 적는다.
  // 단위는 이제 필수가 아니다.
  const missing = [
    !divisionSlug && '부문',
    !editionSlug && '개정판',
    !isEdit && !groupCode && '소속 장·절',
    isEdit && !payload.code && '코드',
    !payload.title && '품셈명',
  ].filter(Boolean) as string[];

  const pending = createItem.isPending || updateItem.isPending;

  // 서버 메시지를 그대로 쓴다 — "코드가 이미 쓰이고 있다" 같은 구체적인 문구를
  // 서버가 이미 만들어 준다. 일반 문구로 덮으면 무엇을 고칠지 알 수 없다.
  const onError = (cause: unknown) => setError(toUserMessage(cause));

  const submit = () => {
    const onSuccess = () => navigate(PATHS.PUMSEM_MANAGE);
    if (isEdit) updateItem.mutate(payload, { onSuccess, onError });
    else createItem.mutate(payload, { onSuccess, onError });
  };

  /**
   * 저장하고 **같은 자리에서 다음 항목**으로 이어간다.
   *
   * 발행본을 순서대로 옮길 때 목록으로 나갔다 다시 들어오는 왕복이 작업의
   * 대부분을 차지한다. 서버가 코드를 매기므로 폼은 비우기만 하면 된다.
   * 단위는 같은 절 안에서 잘 안 바뀌므로 **남겨 둔다**.
   */
  const submitAndNext = () => {
    createItem.mutate(payload, {
      onSuccess: (created) => {
        setForm({ ...EMPTY, unit: form.unit });
        setSaved(created.code);
        setDirty(false);
      },
      onError,
    });
  };

  return (
    <div className="mx-auto w-full max-w-[72rem] px-4 py-10 sm:px-8">
      <header className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-[22px] font-black tracking-tight text-neutral-900 dark:text-neutral-50">
          {isEdit ? '품셈 항목 수정' : '품셈 항목 추가'}
        </h1>
        {revision ? (
          <span className="rounded-md border border-neutral-200 px-2 py-1 text-[12px] text-neutral-600 dark:border-white/12 dark:text-neutral-300">
            {revision.division.title} · {revision.edition.label}
          </span>
        ) : (
          groupCode && (
            <span className="rounded-md border border-neutral-200 px-2 py-1 text-[12px] text-neutral-600 dark:border-white/12 dark:text-neutral-300">
              {divisionSlug} · {groupCode} 아래 · 코드 자동
            </span>
          )
        )}
        {/* 공개 중인 판을 고치고 있다는 사실은 반드시 보여야 한다. */}
        {revision?.edition.isPublished && (
          <span className="rounded-md bg-warning/15 px-2 py-1 text-[12px] font-bold text-amber-700 dark:text-amber-300">
            공개 중인 판입니다 — 저장 즉시 공개 화면에 반영됩니다
          </span>
        )}
        {/* 본문 블록 편집기까지 얹은 긴 폼이다. 표 하나를 붙여넣어 다듬은
            상태가 클릭 한 번에 통째로 날아가면 안 된다. */}
        {leaving ? (
          <span className="ml-auto flex items-center gap-2">
            <span className="text-[12.5px] text-danger">
              저장하지 않은 내용이 사라집니다.
            </span>
            <button
              type="button"
              className={BTN_DANGER}
              onClick={() => navigate(PATHS.PUMSEM_MANAGE)}
            >
              그냥 나가기
            </button>
            <button
              type="button"
              className={BTN_GHOST}
              onClick={() => setLeaving(false)}
            >
              계속 편집
            </button>
          </span>
        ) : (
          <button
            type="button"
            className={`${BTN_GHOST} ml-auto`}
            onClick={() => {
              if (dirty) setLeaving(true);
              else navigate(PATHS.PUMSEM_MANAGE);
            }}
          >
            목록으로
          </button>
        )}
      </header>

      {/* 발행본의 제목 줄을 그대로 붙이면 제목·개정표기가 나뉜다.
          982쪽을 옮기는 작업에서 가장 자주 반복되는 입력이다. */}
      <div className="mb-4 rounded-xl border border-brand-200 bg-brand-50/40 px-4 py-3 dark:border-brand-500/30 dark:bg-brand-500/5">
        <label className={LABEL} htmlFor="pumsem-heading-paste">
          제목 줄 붙여넣기
        </label>
        <input
          id="pumsem-heading-paste"
          className={FIELD}
          placeholder="1-1-2 유도선 설치 및 해체('08년 신설, '17, '21년 보완)"
          onChange={(event) => {
            const parsed = parseHeadingLine(event.target.value);
            if (!parsed) return;
            patchForm({
              // 신설에서는 코드를 서버가 매긴다 — 원문 코드는 참고로만 채운다.
              ...(isEdit ? { code: parsed.code } : {}),
              title: parsed.title,
              marksText: parsed.revisionMarks.join(', '),
            });
            event.target.value = '';
          }}
        />
        <p className="mt-1.5 text-[12px] text-neutral-500">
          품셈명·개정표기로 나눠 아래 칸을 채웁니다.
          {!isEdit && ' 코드는 소속 장·절 아래 다음 번호로 자동 부여됩니다.'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[10rem_1fr_8rem]">
        <div>
          <label className={LABEL} htmlFor="pumsem-code">
            코드
          </label>
          <input
            id="pumsem-code"
            className={FIELD}
            value={isEdit ? form.code : '저장 시 자동'}
            disabled={!isEdit}
            placeholder="6-2-4"
            onChange={(event) => patchForm({ code: event.target.value })}
          />
        </div>
        <div>
          <label className={LABEL} htmlFor="pumsem-title">
            품셈명
          </label>
          <input
            id="pumsem-title"
            className={FIELD}
            value={form.title}
            placeholder="가교화 폴리에틸렌관(PE-X) 배관"
            onChange={(event) => patchForm({ title: event.target.value })}
          />
        </div>
        <div>
          <label className={LABEL} htmlFor="pumsem-unit">
            단위 (선택)
          </label>
          <input
            id="pumsem-unit"
            className={FIELD}
            value={form.unit}
            placeholder="m"
            onChange={(event) => patchForm({ unit: event.target.value })}
          />
        </div>
      </div>

      <p className="mt-2 text-[12px] text-neutral-500">
        {isEdit
          ? '코드를 바꾸면 옛 코드로 들어오는 링크가 자동으로 새 코드로 연결됩니다.'
          : '적용기준·일반사항처럼 품이 아닌 항목은 단위를 비워 둡니다.'}
      </p>

      <div className="mt-4">
        <label className={LABEL} htmlFor="pumsem-marks">
          개정 표기 — 제목 괄호 안 원문, 쉼표로 구분
        </label>
        <input
          id="pumsem-marks"
          className={FIELD}
          value={form.marksText}
          placeholder="'08년 신설, '17, '21년 보완"
          onChange={(event) => patchForm({ marksText: event.target.value })}
        />
      </div>

      <h2 className="mt-8 mb-1 text-[15px] font-bold text-neutral-900 dark:text-neutral-100">
        본문
      </h2>
      <p className="mb-3 text-[12.5px] text-neutral-500">
        문단·번호목록·표·그림·산식을 발행본 순서 그대로 세웁니다. 표가 없는
        항목(적용기준·일반사항 계열)은 문단과 목록만으로 충분합니다.
      </p>
      <PumsemBodyEditor
        body={form.body}
        onChange={(body) => patchForm({ body })}
      />

      <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div>
          <label className={LABEL} htmlFor="pumsem-notes">
            본문에 배치하지 않은 [주] — 한 줄에 하나
          </label>
          <textarea
            id="pumsem-notes"
            className={`${FIELD} h-40 leading-7`}
            value={form.notesText}
            placeholder={'① 본 품은 …\n② 절단, 배관 …'}
            onChange={(event) =>
              patchForm({
                // `[주] ① … ② …` 를 한 줄로 붙여넣어도 번호 단위로 갈린다.
                // 번호는 남긴다 — 실무자가 "[주] ②를 보라"로 인용한다.
                notesText: splitNotes(event.target.value).join('\n'),
              })
            }
          />
          <p className="mt-1.5 text-[12px] text-neutral-500">
            원문 그대로 넣습니다. <code>[주] ① … ② …</code> 를 통째로 붙여넣으면
            번호 단위로 나눕니다. 여기 [주]는 항목 맨 아래에 나옵니다 — 표마다
            [주]가 따로 있으면 본문의 <b>+ [주]</b> 블록으로 원문 자리에 두세요.
          </p>
          {form.notesText.trim() && (
            <button
              type="button"
              className={`${BTN_GHOST} mt-2`}
              // 옮긴 뒤 본문에서 ↑↓ 로 제자리를 찾아 준다. 기존 항목의 [주]를
              // 원문 배치로 바꾸는 가장 짧은 길이다.
              onClick={() =>
                patchForm({
                  body: insertBlock(form.body, [], {
                    type: 'notes',
                    notes: toLines(form.notesText),
                  }),
                  notesText: '',
                })
              }
            >
              본문 끝에 [주] 블록으로 옮기기
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className={LABEL} htmlFor="pumsem-includes">
              이 품에 포함 — 한 줄에 하나
            </label>
            <textarea
              id="pumsem-includes"
              className={`${FIELD} h-20 leading-6`}
              value={form.includesText}
              onChange={(event) =>
                patchForm({ includesText: event.target.value })
              }
            />
          </div>
          <div>
            <label className={LABEL} htmlFor="pumsem-excludes">
              별도 계상 — 한 줄에 하나
            </label>
            <textarea
              id="pumsem-excludes"
              className={`${FIELD} h-20 leading-6`}
              value={form.excludesText}
              onChange={(event) =>
                patchForm({ excludesText: event.target.value })
              }
            />
          </div>
          <div>
            <label className={LABEL} htmlFor="pumsem-scope">
              적용 범위
            </label>
            <input
              id="pumsem-scope"
              className={FIELD}
              value={form.scope}
              placeholder="옥내 난방배관 한정"
              onChange={(event) => patchForm({ scope: event.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <label className={LABEL} htmlFor="pumsem-summary">
          개정 요약
        </label>
        <input
          id="pumsem-summary"
          className={FIELD}
          value={form.changeSummary}
          placeholder="배관공 품 변경, 50mm 신설"
          onChange={(event) => patchForm({ changeSummary: event.target.value })}
        />
        <p className="mt-1.5 text-[12px] text-neutral-500">
          비워두면 개정 여부만 표시되고 요약은 남지 않습니다. 자동으로 지어내지
          않습니다.
        </p>
      </div>

      {missing.length > 0 && (
        <p className="mt-6 text-[13px] text-amber-700 dark:text-amber-300">
          저장하려면 {missing.join(' · ')} 이(가) 필요합니다.
        </p>
      )}
      {error && (
        <p className="mt-3 text-[13px] text-danger" role="alert">
          {error}
        </p>
      )}
      {saved && (
        <p
          className="mt-3 text-[13px] text-emerald-700 dark:text-emerald-300"
          role="status"
        >
          {saved} 저장했습니다. 이어서 다음 항목을 입력하세요.
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          type="button"
          className={BTN_PRIMARY}
          disabled={missing.length > 0 || pending}
          onClick={submit}
        >
          {pending ? '저장 중…' : '저장'}
        </button>
        {!isEdit && (
          <button
            type="button"
            className={BTN_GHOST}
            disabled={missing.length > 0 || pending}
            onClick={submitAndNext}
          >
            저장하고 다음 항목
          </button>
        )}
        {/* 확인 없이 나가면 편집 중 오클릭 한 번에 항목이 빠진다. 그리고
            예전엔 `onError` 가 없어서, 실패하면 아무 일도 안 일어난 것처럼
            화면이 그대로 멈췄다 — 이 폼의 다른 mutation 은 전부 띄운다. */}
        {isEdit &&
          revisionId &&
          (confirmingRemove ? (
            <>
              <button
                type="button"
                className={BTN_DANGER}
                disabled={removeItem.isPending}
                onClick={() =>
                  removeItem.mutate(revisionId, {
                    onSuccess: () => navigate(PATHS.PUMSEM_MANAGE),
                    onError: (cause) => {
                      setConfirmingRemove(false);
                      setError(toUserMessage(cause));
                    },
                  })
                }
              >
                {removeItem.isPending ? '제외하는 중…' : '정말 제외'}
              </button>
              <button
                type="button"
                className={BTN_GHOST}
                onClick={() => setConfirmingRemove(false)}
              >
                취소
              </button>
            </>
          ) : (
            <button
              type="button"
              className={BTN_DANGER}
              onClick={() => setConfirmingRemove(true)}
            >
              이 판에서 제외
            </button>
          ))}
      </div>
      {isEdit && (
        <p className="mt-2 text-[12px] text-neutral-500">
          제외해도 다른 개정판의 같은 항목과 그 링크는 그대로 남습니다.
        </p>
      )}
    </div>
  );
}
